import React, { useState } from "react";
import { Hash, Plus, X } from "react-feather";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cx from "classnames";

function SortableHashtag({
  hashtag,
  onRemove,
  aboveMaxLength,
  displayOnly,
  isExcess,
}: {
  hashtag: string;
  onRemove: () => void;
  aboveMaxLength: boolean;
  displayOnly: boolean;
  isExcess: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
  } = useSortable({ id: hashtag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        className={cx(
          "badge badge-outline border-1",
          isExcess ? "badge-error border-error" : "badge-neutral"
        )}
      >
        {!displayOnly && (
          <div className="cursor-pointer" onClick={onRemove}>
            <X size={16} />
          </div>
        )}
        <div className="cursor-grab" ref={setActivatorNodeRef} {...listeners}>
          {hashtag}
        </div>
      </div>
    </div>
  );
}

export interface HashtagListProps {
  hashtags: string[];
  onHashtagsChange: (hashtags: string[]) => void;
  aboveMaxLength?: boolean;
  placeholder?: string;
  className?: string;
  displayOnly?: boolean;
  content?: string;
  maxContentLength?: number;
}

export function HashtagList({
  hashtags,
  onHashtagsChange,
  aboveMaxLength = false,
  placeholder = "Add hashtag",
  className,
  displayOnly = false,
  content,
  maxContentLength,
}: HashtagListProps) {
  const [newHashtag, setNewHashtag] = useState("");

  console.log("HashtagList rendered with hashtags:", hashtags);

  // Calculate which hashtags are excess
  const calculateExcessHashtags = (): number => {
    if (!aboveMaxLength || !content?.length || !maxContentLength) {
      return 0;
    }

    // Find hashtags that are in the hashtags array but not in the content
    const hashtagsNotInContent = hashtags.filter(
      (hashtag) => !content.includes(hashtag)
    );
    const additionalHashtagLength = hashtagsNotInContent.reduce(
      (sum, hashtag) => sum + hashtag.length + 1,
      0
    ); // +1 for space

    const totalContentLength = content.length + additionalHashtagLength;
    const charactersOver = totalContentLength - maxContentLength - 5;
    if (charactersOver <= 0) return 0;

    let charactersToRemove = 0;
    let hashtagsToRemove = 0;

    // Calculate from the end (last hashtags first)
    for (let i = hashtags.length - 1; i >= 0; i--) {
      const hashtagLength = hashtags[i].length + 1; // +1 for space separator
      charactersToRemove += hashtagLength;
      hashtagsToRemove++;

      if (charactersToRemove >= charactersOver) {
        break;
      }
    }

    return hashtagsToRemove;
  };

  const excessHashtagCount = calculateExcessHashtags();
  const excessHashtagStartIndex = hashtags.length - excessHashtagCount;

  const handleRemoveExcessHashtags = () => {
    if (excessHashtagCount > 0) {
      const newHashtags = hashtags.slice(
        0,
        hashtags.length - excessHashtagCount
      );
      onHashtagsChange(newHashtags);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = hashtags.indexOf(active.id as string);
      const newIndex = hashtags.indexOf(over.id as string);
      const newHashtags = arrayMove(hashtags, oldIndex, newIndex);
      onHashtagsChange(newHashtags);
    }
  };

  const handleRemoveHashtag = (hashtag: string) => {
    const newHashtags = hashtags.filter((h) => h !== hashtag);
    onHashtagsChange(newHashtags);
  };

  const handleAddHashtag = () => {
    if (newHashtag.trim()) {
      const formattedHashtag = (
        newHashtag.trim().startsWith("#") || newHashtag.trim().startsWith("@")
          ? newHashtag.trim()
          : `#${newHashtag.trim()}`
      ).replace(/\s+/g, "");
      onHashtagsChange([...hashtags, formattedHashtag]);
      setNewHashtag("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddHashtag();
    }
  };

  return (
    <div className={cx("flex flex-wrap gap-2", className)}>
      {hashtags.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={hashtags} strategy={rectSortingStrategy}>
            {hashtags.map((hashtag, index) => (
              <SortableHashtag
                key={hashtag}
                hashtag={hashtag}
                onRemove={() => handleRemoveHashtag(hashtag)}
                aboveMaxLength={aboveMaxLength}
                displayOnly={displayOnly}
                isExcess={
                  index >= excessHashtagStartIndex && excessHashtagCount > 0
                }
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {!displayOnly && excessHashtagCount > 0 && (
        <button
          type="button"
          className="btn btn-error btn-sm gap-1"
          onClick={handleRemoveExcessHashtags}
        >
          <X size={14} />
          Remove {excessHashtagCount} hashtag{excessHashtagCount > 1 ? "s" : ""}
        </button>
      )}
      {!displayOnly && (
        <div className="flex gap-2">
          <label className="input input-xs border-1 rounded-full w-40 border-neutral text-neutral">
            <input
              type="text"
              className="p-0"
              placeholder={placeholder}
              value={newHashtag}
              onChange={(e) => setNewHashtag(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <span className="label mr-0">
              {newHashtag?.length > 0 ? (
                <button
                  className="btn btn-xs btn-outline bg-transparent border-none py-0.5 px-2"
                  onClick={handleAddHashtag}
                  type="button"
                >
                  <Plus size={12} className="cursor-pointer" />
                  Add
                </button>
              ) : (
                <Hash size={12} />
              )}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
